/**
 * Date Formatters
 *
 * Locale-aware date formatting using Intl.DateTimeFormat.
 */

import type { DateFormatStyle } from '../types';

/**
 * How to format: one of the four named styles, or explicit Intl options for a
 * shape the styles do not cover (month + year, weekday, day + month). Either
 * way the LOCALE is the caller's app locale — never the browser's. (F34 · F49)
 */
export type DateFormat = DateFormatStyle | CalendarDayStyle | Intl.DateTimeFormatOptions;

/**
 * A calendar date as a list says it (owner, 20 Oct 2026 — D1: numbers are for
 * forms): the day and the month in words, in UTC like `CALENDAR_DATE_FORMAT`.
 *   'calendarDay'       "2 Nov 2026" · "2 nov. 2026" · "2 de nov. de 2026"
 *   'calendarDayMonth'  the same without the year — where a heading above
 *                       already says it ("2 Nov" under "2026")
 */
export type CalendarDayStyle = 'calendarDay' | 'calendarDayMonth';

/** How to format a moment's time — the named styles or Intl options; a time has no calendar-day form. */
export type TimeFormat = DateFormatStyle | Intl.DateTimeFormatOptions;

function resolve(
  format: TimeFormat,
  map: Record<DateFormatStyle, Intl.DateTimeFormatOptions>
): Intl.DateTimeFormatOptions {
  return typeof format === 'string' ? map[format] : format;
}

// ═══════════════════════════════════════════════════════════════════════════
// FORMAT OPTIONS
// ═══════════════════════════════════════════════════════════════════════════

const DATE_FORMAT_MAP: Record<DateFormatStyle, Intl.DateTimeFormatOptions> = {
  short: { dateStyle: 'short' },
  medium: { dateStyle: 'medium' },
  long: { dateStyle: 'long' },
  full: { dateStyle: 'full' },
};

/**
 * A calendar date — a `YYYY-MM-DD` birthday or expiry, not an instant. Such a
 * string parses as UTC midnight, so formatting it in the reader's zone shows
 * the day BEFORE anywhere west of UTC (all of LatAm). Read it in UTC:
 * `formatDate('1988-03-14', CALENDAR_DATE_FORMAT)` is 14 March everywhere.
 */
export const CALENDAR_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  dateStyle: 'medium',
  timeZone: 'UTC',
};

const DATETIME_FORMAT_MAP: Record<DateFormatStyle, Intl.DateTimeFormatOptions> = {
  short: { dateStyle: 'short', timeStyle: 'short' },
  medium: { dateStyle: 'medium', timeStyle: 'short' },
  long: { dateStyle: 'long', timeStyle: 'medium' },
  full: { dateStyle: 'full', timeStyle: 'long' },
};

const TIME_FORMAT_MAP: Record<DateFormatStyle, Intl.DateTimeFormatOptions> = {
  short: { timeStyle: 'short' },
  medium: { timeStyle: 'medium' },
  long: { timeStyle: 'long' },
  full: { timeStyle: 'full' },
};

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

function toDate(date: Date | string | number): Date {
  if (date instanceof Date) return date;
  return new Date(date);
}

// ═══════════════════════════════════════════════════════════════════════════
// FORMATTERS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Format a date (date only, no time)
 *
 * @param date - Date to format
 * @param locale - Locale string (e.g., 'en-US', 'pt-PT')
 * @param style - 'short' | 'medium' | 'long' | 'full', or explicit Intl options
 *
 * @example
 * formatDate(new Date(), 'en-US') // "Jan 15, 2024"
 * formatDate(new Date(), 'pt-PT', 'long') // "15 de janeiro de 2024"
 */
export function formatDate(
  date: Date | string | number,
  locale: string,
  style: DateFormat = 'medium'
): string {
  try {
    const d = toDate(date);
    if (style === 'calendarDay' || style === 'calendarDayMonth') {
      return formatCalendarDay(d, locale, style === 'calendarDay');
    }
    return new Intl.DateTimeFormat(locale, resolve(style, DATE_FORMAT_MAP)).format(d);
  } catch {
    return String(date);
  }
}

/**
 * The day and the month in words, the year when asked. ICU's European
 * Portuguese has no worded "day + short month" pattern — it answers "2/11/2026"
 * — so there the date is said from its parts, as pt-PT print says it: "2 nov.
 * 2026". Every other locale's own pattern is kept.
 */
function formatCalendarDay(d: Date, locale: string, withYear: boolean): string {
  const resolved = new Intl.DateTimeFormat(locale).resolvedOptions().locale;
  if (resolved === 'pt-PT') {
    const part = (options: Intl.DateTimeFormatOptions) =>
      new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(d);
    const said = `${part({ day: 'numeric' })} ${part({ month: 'short' })}`;
    return withYear ? `${said} ${part({ year: 'numeric' })}` : said;
  }
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  }).format(d);
}

/**
 * Format a date with time
 *
 * @param date - Date to format
 * @param locale - Locale string (e.g., 'en-US', 'pt-PT')
 * @param style - 'short' | 'medium' | 'long' | 'full', or explicit Intl options
 *
 * @example
 * formatDateTime(new Date(), 'en-US') // "Jan 15, 2024, 2:30 PM"
 * formatDateTime(new Date(), 'de-DE', 'long') // "15. Januar 2024, 14:30:00"
 */
export function formatDateTime(
  date: Date | string | number,
  locale: string,
  style: TimeFormat = 'medium'
): string {
  try {
    const d = toDate(date);
    return new Intl.DateTimeFormat(locale, resolve(style, DATETIME_FORMAT_MAP)).format(d);
  } catch {
    return String(date);
  }
}

/**
 * Format time only (no date)
 *
 * @param date - Date to extract time from
 * @param locale - Locale string (e.g., 'en-US', 'pt-PT')
 * @param style - 'short' | 'medium' | 'long' | 'full', or explicit Intl options
 *
 * @example
 * formatTime(new Date(), 'en-US') // "2:30 PM"
 * formatTime(new Date(), 'de-DE', 'medium') // "14:30:00"
 */
export function formatTime(
  date: Date | string | number,
  locale: string,
  style: TimeFormat = 'short'
): string {
  try {
    const d = toDate(date);
    return new Intl.DateTimeFormat(locale, resolve(style, TIME_FORMAT_MAP)).format(d);
  } catch {
    return String(date);
  }
}

/**
 * A time zone's name for people, in `locale` — "Brasília Time", "Horário de
 * Brasília" — rather than its IANA id. Falls back to the long specific name
 * where the runtime has no generic one (some Hermes builds), then to the id.
 *
 * @example
 * formatTimeZoneName('America/Sao_Paulo', 'en-US') // "Brasilia Time"
 */
export function formatTimeZoneName(
  timeZone: string,
  locale: string,
  date: Date = new Date()
): string {
  for (const timeZoneName of ['longGeneric', 'long'] as const) {
    try {
      const name = timeZoneNameOf(
        new Intl.DateTimeFormat(locale, { timeZone, timeZoneName }).formatToParts(date)
      );
      if (name) return name;
    } catch {
      // This runtime lacks the style (or the zone) — try the next, then the id.
    }
  }
  return timeZone;
}

/**
 * The zone's name out of a formatted date's parts: everything from its first
 * `timeZoneName` part to its last. Hermes (the phone's engine) splits a name
 * into one part per word and mislabels the words between them —
 * "Hora da Europa Ocidental" came back as `timeZoneName` "Hora", `year` "da",
 * `timeZoneName` "Europa"… — so taking the first part showed "Western" for
 * Western European Time. V8 and the browsers give one part; this reads both.
 */
function timeZoneNameOf(parts: Intl.DateTimeFormatPart[]): string | null {
  const first = parts.findIndex((p) => p.type === 'timeZoneName');
  if (first === -1) return null;
  let last = first;
  parts.forEach((p, i) => {
    if (p.type === 'timeZoneName') last = i;
  });
  const name = parts
    .slice(first, last + 1)
    .map((p) => p.value)
    .join('')
    .trim();
  return name || null;
}

/**
 * The IANA time zone this device is set to (`America/Sao_Paulo`), or `null`
 * where the runtime does not report one.
 */
export function getDeviceTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}
